import React, { useState, useEffect } from 'react'
import { useLocation, Link } from 'react-router-dom'
import { getAllBlogPosts, type BlogPost as BlogPostType } from '@/lib/markdown'

const BlogPost: React.FC = () => {
  const location = useLocation()
  const slug = location.pathname.replace('/blog/', '')
  const [post, setPost] = useState<BlogPostType | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  useEffect(() => {
    const loadPost = async () => {
      try {
        const posts = await getAllBlogPosts()
        const foundPost = posts.find(p => p.slug === slug)
        
        if (foundPost) {
          setPost(foundPost)
        } else {
          setNotFound(true)
        }
      } catch (error) {
        console.error('Error loading blog post:', error)
        setNotFound(true)
      } finally {
        setLoading(false)
      }
    }
    
    loadPost()
  }, [slug])

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    } catch {
      return dateString
    }
  }

  if (loading) {
    return (
      <div className="max-w-2xl">
        <div className="font-mono text-sm">Loading blog post...</div>
      </div>
    )
  }

  if (notFound || !post) {
    return (
      <div className="max-w-2xl">
        <h1 className="text-3xl font-bold mb-8 font-mono">Post Not Found</h1>
        <div className="font-mono text-sm mb-4">
          The blog post you're looking for doesn't exist.
        </div>
        <Link to="/blog" className="button-minimal">
          ← Back to Blog
        </Link>
      </div>
    )
  }

  return (
    <div className="max-w-2xl">
      {/* Back to blog link */}
      <div className="mb-6">
        <Link to="/blog" className="text-xs font-mono link-minimal">
          ← Back to Blog
        </Link>
      </div>

      {/* Post header */}
      <article>
        <header className="mb-8 border-b border-black pb-6">
          <div className="text-xs font-mono text-gray-500 mb-2">
            {formatDate(post.date)}
          </div>
          
          <h1 className="text-2xl font-bold font-mono mb-4">
            {post.title}
          </h1>
          
          {post.summary && (
            <p className="text-sm font-mono text-gray-700 mb-4">
              {post.summary}
            </p>
          )}
          
          {post.tags && post.tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag, index) => (
                <span 
                  key={index}
                  className="text-xs font-mono border border-black px-2 py-1"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </header>

        {/* Post content */}
        <div 
          className="font-mono text-sm leading-relaxed prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      </article>

      {/* Footer */}
      <footer className="mt-12 border-t border-black pt-8">
        <div className="flex justify-between items-center font-mono text-xs">
          <Link to="/blog" className="link-minimal">
            ← Back to Blog
          </Link>
          <div className="text-gray-500">
            Published {formatDate(post.date)}
          </div>
        </div>
      </footer>
    </div>
  )
}

export default BlogPost 